import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useLocation } from '@tanstack/react-router';
import { Bell } from 'lucide-react';

import logoConnectDark from '@/assets/logo-connect-dark.png';
import logoConnectMark from '@/assets/logo-connect-mark.png';
import logoConnect from '@/assets/logo-connect.png';
import { GlobalAdd } from '@/components/layout/global-add';
import { GlobalSearch } from '@/components/layout/global-search';
import { UserMenu } from '@/components/layout/user-menu';
import { Button } from '@/components/new-ui/button';
import { useTenantBranding } from '@/components/providers/tenant-branding-provider';

/** Flip to true once notifications exist, the bell below is already in place. */
const SHOW_NOTIFICATIONS = false;

type LogoLinkProps = {
  className: string;
  isHome: boolean;
  label: string;
  children: ReactNode;
};

// Logo returns to the estates list, which is the home page; on that page it is not clickable,
// so it does not reset the list filters
const LogoLink = ({ className, isHome, label, children }: LogoLinkProps) =>
  isHome ? (
    <div className={className}>{children}</div>
  ) : (
    <Link
      to="/estates"
      aria-label={label}
      className={`${className} rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {children}
    </Link>
  );

export const Header = () => {
  const { t } = useTranslation();
  const { logoUrl } = useTenantBranding();
  const isHome = useLocation({
    select: (location) => location.pathname.replace(/\/$/, '') === '/estates',
  });
  const goHomeLabel = t('shellGoHome');

  return (
    <header className="new-ui shrink-0 border-b border-border bg-background">
      {/* Three columns so search stays centered regardless of what sits on either side */}
      <div className="grid h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 md:grid-cols-[1fr_minmax(0,36rem)_1fr] md:gap-4 md:px-6">
        {/* Logo Area */}
        {logoUrl ? (
          <LogoLink
            isHome={isHome}
            label={goHomeLabel}
            className="flex w-14 shrink-0 flex-col items-center justify-center justify-self-start"
          >
            <img
              src={logoUrl}
              alt={t('shellTenantLogo')}
              className="mb-0.5 h-8 w-8 shrink-0 rounded object-contain"
            />
            <span className="text-[9px] font-bold tracking-wide text-foreground">Connect</span>
          </LogoLink>
        ) : (
          // Default brand logo; the dark variant has a light wordmark so it stays readable
          <LogoLink
            isHome={isHome}
            label={goHomeLabel}
            className="flex shrink-0 items-center justify-self-start"
          >
            {/* Phones show only the round mark so search keeps its width */}
            <img
              src={logoConnectMark}
              alt="Connect"
              className="h-8 w-8 max-w-none shrink-0 sm:hidden"
            />
            <img
              src={logoConnect}
              alt="Connect"
              className="hidden h-8 w-auto max-w-none shrink-0 sm:block dark:sm:hidden"
            />
            <img
              src={logoConnectDark}
              alt="Connect"
              className="hidden h-8 w-auto max-w-none shrink-0 dark:sm:block"
            />
          </LogoLink>
        )}

        {/* Search and Actions */}
        <div className="flex min-w-0 items-center justify-center gap-3">
          <GlobalSearch />
          <GlobalAdd />
        </div>

        <div className="justify-self-end">
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            {SHOW_NOTIFICATIONS ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Bell />
              </Button>
            ) : null}

            {/* Account menu: avatar only, right of notifications */}
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
};
